import { Anchor, Progress, Stack, Table, Text } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { TravelActivityType, TravelDayBalanceTrip, formatTravelRegistration } from '../model/TravelRequestTypes';
import { TravelRegistryTypeBadge } from './TravelRegistryTypeBadge';
import { TravelRequestStatusBadge } from './TravelRequestStatusBadge';

export const travelFormationActivityTypes: TravelActivityType[] = [
  'SCHOLARSHIP',
  'PROFESSIONAL_DEVELOPMENT',
  'EXPERIENCE_EXCHANGE',
];

export function travelDayBalanceColor(total: number, cap: number) {
  if (total > cap) return 'red';
  return total === cap ? 'yellow' : 'green';
}

interface TravelDayBalanceStatProps {
  label: string;
  value: ReactNode;
  used?: number;
  cap?: number;
  hint?: ReactNode;
}

export function TravelDayBalanceStat({ label, value, used, cap, hint }: TravelDayBalanceStatProps) {
  return (
    <Stack gap="4px">
      <Text
        size="xs"
        c="dimmed"
      >
        {label}
      </Text>
      <Text
        size="lg"
        fw="500"
      >
        {value}
      </Text>
      {used != null && cap != null && cap > 0 && (
        <Progress
          value={Math.min((used / cap) * 100, 100)}
          color={travelDayBalanceColor(used, cap)}
          size="sm"
          aria-label={label}
        />
      )}
      {hint}
    </Stack>
  );
}

export function TravelDayBalanceExceeded({ used, cap }: { used: number; cap: number }) {
  const { t } = useTranslation();

  if (used <= cap) return null;

  return (
    <Text
      size="xs"
      c="red"
      fw={600}
    >
      {t('travel.requests.dayBalance.exceededBy', { count: used - cap })}
    </Text>
  );
}

interface TravelDayBalanceTripsProps {
  trips: TravelDayBalanceTrip[];
}

export function TravelDayBalanceTrips({ trips }: TravelDayBalanceTripsProps) {
  const { t } = useTranslation();

  return (
    <Table.ScrollContainer minWidth={720}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('travel.requests.dayBalance.trips.registration')}</Table.Th>
            <Table.Th>{t('travel.requests.dayBalance.trips.name')}</Table.Th>
            <Table.Th>{t('travel.requests.dayBalance.trips.motivation')}</Table.Th>
            <Table.Th>{t('travel.requests.dayBalance.trips.paidDays')}</Table.Th>
            <Table.Th>{t('travel.requests.dayBalance.trips.vacationDays')}</Table.Th>
            <Table.Th>{t('travel.requests.dayBalance.trips.unpaidDays')}</Table.Th>
            <Table.Th>{t('travel.requests.dayBalance.trips.status')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {trips.map(({ registrationNumber, registrationYear, registrationDate, registryType, ...trip }) => {
            const registration =
              registrationNumber != null && registrationYear != null
                ? formatTravelRegistration({ registrationNumber, registrationYear, registrationDate })
                : null;
            return (
              <Table.Tr key={trip.id}>
                <Table.Td ta="center">
                  {registration == null && '-'}
                  {registration != null && (
                    <Stack
                      gap={4}
                      align="center"
                      w={260}
                    >
                      <Text inherit>{registration}</Text>
                      {registryType != null && <TravelRegistryTypeBadge registryType={registryType} />}
                    </Stack>
                  )}
                </Table.Td>
                <Table.Td>
                  <Anchor
                    component={Link}
                    to={`/travel/requests/${trip.id}`}
                    size="sm"
                  >
                    {trip.name ?? `#${trip.id}`}
                  </Anchor>
                </Table.Td>
                <Table.Td>
                  {trip.absenceMotivation == null
                    ? '-'
                    : t(`travel.requests.dayBalance.motivationShort.${trip.absenceMotivation}`)}
                </Table.Td>
                <Table.Td>
                  {trip.professionalDevelopmentDays > 0
                    ? `${trip.paidDays} (${t('travel.requests.dayBalance.trips.professionalDevelopment')} ${trip.professionalDevelopmentDays})`
                    : trip.paidDays}
                </Table.Td>
                <Table.Td>{trip.vacationDays}</Table.Td>
                <Table.Td>{trip.unpaidDays}</Table.Td>
                <Table.Td>
                  <TravelRequestStatusBadge status={trip.status} />
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
